/**
 * GTM/GA4 ecommerce helpers for dataLayer pushes.
 * item_category from locationNames/activityNames/accommodationNames/transportNames.
 */
type CategoriesShape = {
    locationNames?: string[];
    activityNames?: string[];
    accommodationNames?: string[];
    transportNames?: string[];
};

export function getViewItemCategories(categories: CategoriesShape | undefined): {
    item_category: string;
    item_category2: string;
} {
    const c = categories ?? {};
    const mapping: { key: keyof CategoriesShape; label: string }[] = [
        { key: "locationNames", label: "Destinations" },
        { key: "activityNames", label: "Experiences" },
        { key: "accommodationNames", label: "Accommodations" },
        { key: "transportNames", label: "Transport" },
    ];
    const categoryLabels: string[] = [];
    const category2Values: string[] = [];
    for (const { key, label } of mapping) {
        const arr = c[key];
        if (Array.isArray(arr) && arr.length > 0) {
            categoryLabels.push(label);
            category2Values.push(...arr);
        }
    }
    return {
        item_category: categoryLabels.join(", ") || "Tours",
        item_category2: category2Values.join(", "),
    };
}

/**
 * GA4 select_item: push when user selects a fare (e.g. in availability panel).
 * priceOverride: use when price comes from availability RRP; otherwise derived from priceGroup.rrp.
 */
export function pushSelectItem(
    product: AddToCartProductSnapshot,
    priceGroup: { rrp?: string | number | null },
    priceOverride?: number | null,
): void {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    const rawPrice = priceOverride ?? priceGroup?.rrp ?? "0";
    const price = typeof rawPrice === "number" ? rawPrice : parseFloat(String(rawPrice).replace(/[^\d.-]/g, "")) || 0;
    const { item_category, item_category2 } = getViewItemCategories(product.categories);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: "select_item",
        ecommerce: {
            items: [
                {
                    item_id: product.productId,
                    item_variant: String(product.tourCode ?? ""),
                    item_name: product.name,
                    item_brand: product.supplier ?? "",
                    item_category,
                    item_category2,
                    item_location_id: product.location ?? "",
                    duration: product.duration ?? "",
                    price,
                    quantity: 1,
                },
            ],
        },
    });
}

/** Product-like shape for add_to_cart (no dependency on IProductDetail). */
export type AddToCartProductSnapshot = {
    productId: string;
    name: string;
    supplier?: string | null;
    categories?: CategoriesShape;
    tourCode?: string | number;
    location?: string;
    duration?: string;
};

/**
 * GA4 add_to_cart: push when user clicks "Book Now" or "Add to Cart" in availability modal.
 */
export function pushAddToCart(
    product: AddToCartProductSnapshot,
    options: { value: number; travelDate: string; ctaClick: "Book Now" | "Add to Cart" },
): void {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    const { value, travelDate, ctaClick } = options;
    const { item_category, item_category2 } = getViewItemCategories(product.categories);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
            currency: "AUD",
            value,
            items: [
                {
                    item_id: product.productId,
                    item_name: product.name,
                    item_brand: product.supplier ?? "",
                    item_category,
                    item_category2,
                    item_variant: String(product.tourCode ?? ""),
                    item_location_id: product.location ?? "",
                    price: value,
                    quantity: 1,
                    duration: product.duration ?? "",
                    travel_date: travelDate,
                    cta_click: ctaClick,
                },
            ],
        },
    });
}

/**
 * GA4 begin_checkout: push when user clicks the main payment/checkout CTA.
 */
export function pushBeginCheckout(
    product: AddToCartProductSnapshot,
    options: { value: number; travelDate: string; ctaClick: string },
): void {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    const { value, travelDate, ctaClick } = options;
    const { item_category, item_category2 } = getViewItemCategories(product.categories);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
            currency: "AUD",
            value,
            items: [
                {
                    item_id: product.productId,
                    item_name: product.name,
                    item_brand: product.supplier ?? "",
                    item_category,
                    item_category2,
                    item_variant: String(product.tourCode ?? ""),
                    item_location_id: product.location ?? "",
                    price: value,
                    quantity: 1,
                    duration: product.duration ?? "",
                    travel_date: travelDate,
                    cta_click: ctaClick,
                },
            ],
        },
    });
}

/**
 * GA4 purchase: push when order is successfully completed.
 * Uses a single-item summary based on the last checkout snapshot.
 */
export function pushPurchase(
    product: AddToCartProductSnapshot,
    options: { transactionId: string; value?: number; travelDate?: string },
): void {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    const { transactionId, value, travelDate } = options;
    const { item_category, item_category2 } = getViewItemCategories(product.categories);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: "purchase",
        ecommerce: {
            transaction_id: transactionId,
            value: value ?? undefined,
            tax: 0,
            currency: "AUD",
            items: [
                {
                    item_id: product.productId,
                    item_name: product.name,
                    item_brand: product.supplier ?? "",
                    item_category,
                    item_category2,
                    item_variant: String(product.tourCode ?? ""),
                    item_location_id: product.location ?? "",
                    price: value ?? undefined,
                    quantity: 1,
                    duration: product.duration ?? "",
                    travel_date: travelDate,
                },
            ],
        },
    });
}
